// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { Auth2Service } from '@core/auth/auth.service';
import { DataStore } from '@core/store/data.store';
import { Router, UrlTree } from '@angular/router';
import { SignalRHub } from '@core/services/signalr.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { SaraEmptyStateComponent } from '@shared/ui/sara-empty-state/sara-empty-state.component';
import { AUTH_ERROR_HINT, AUTH_ERROR_RETRY, AUTH_ERROR_TITLE } from '@shared/models/access';
import { resolveSplashScreen } from '@core/auth/splash-screen';
import { ReplayLoadingScreenComponent } from '@shared/ui/replay-loading-screen/replay-loading-screen.component';
import { Platform } from '@ionic/angular';
import { RootStore } from '@core/store/root.store';
import { FlightsStore } from '@features/flights/store/flights.store';
import { persistImpersonation, restoreImpersonation } from '@core/auth/impersonation';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  // While booting toward the replay route, paint the backdrop base on the host
  // so any brief empty-router-outlet frame (the app splash swapped out before
  // the lazy replay chunk mounts) stays tone-continuous instead of flashing the
  // flat app background. --chart-editor-blue adapts to light/dark on its own,
  // and ReplayComponent's own backdrop covers it once it mounts. (Inline styles
  // rather than the pre-existing, unlinked app.component.scss, which is dead.)
  styles: [':host(.standalone-boot){display:block;height:100%;background:var(--chart-editor-blue)}'],
  host: { '[class.standalone-boot]': 'bootsToStandaloneRoute' },
  imports: [
    IonApp,
    IonRouterOutlet,
    MatProgressBarModule,
    MatButtonModule,
    SaraEmptyStateComponent,
    ReplayLoadingScreenComponent,
  ],
})
export class AppComponent implements OnInit {
  private authService = inject(Auth2Service);
  private router = inject(Router);
  private signalR = inject(SignalRHub);
  private platform = inject(Platform);
  private _routerInitialized = false;
  // Whether THIS tab is bound for the standalone full-screen replay route.
  // Snapshotted once at construction because on a fresh replay tab the OAuth
  // round-trip returns to the origin root (`/`, see auth.service) and the app
  // only navigates to `/replay` AFTER claims land — so the live router URL is
  // `/` for most of the boot. `redirectAfterLogin` (written before the redirect
  // in auth.service.getRedirectUri, consumed in the claims effect) preserves the
  // real deep-link target across that window; `location.pathname` covers the
  // pre-redirect + native (in-app nav) cases. Only `/replay` — sample-flights
  // keeps its own boot handling so we don't hand off to a different loader.
  protected readonly bootsToStandaloneRoute = ((): boolean => {
    if (window.location.pathname.startsWith('/replay')) return true;
    try {
      // Guarded: this runs at construction, and localStorage access throws in
      // some privacy/sandbox modes — a false here just falls back to the app
      // spinner, never a boot crash.
      return (window.localStorage.getItem('redirectAfterLogin') ?? '').startsWith('/replay');
    } catch {
      return false;
    }
  })();
  readonly ds = inject(DataStore);
  readonly rs = inject(RootStore);
  readonly fs = inject(FlightsStore);

  // Which top-level screen the shell shows. Delegates to the pure
  // resolveSplashScreen so the (twice-buggy) screen-selection logic is unit
  // tested in isolation — see splash-screen.spec.ts.
  readonly splashScreen = computed(() =>
    resolveSplashScreen({
      hasClaims: !!this.authService.ergsClaims(),
      loginProgress: this.authService.loginProgress(),
      claimsLoaded: this.authService.claimsLoaded(),
      userLoading: this.ds.user().loading,
      isAuthorizedUser: !!this.ds.isAuthorizedUser(),
    }),
  );

  // What the shell actually renders. While a replay tab is still booting, show
  // the replay route's OWN loader (the shared <replay-loading-screen>) in place
  // of the app spinner — so the user sees a single continuous loader instead of
  // the app spinner stacked in front of the replay loader. We render the loader
  // directly (NOT the router outlet) because the router hasn't reached /replay
  // yet; letting the outlet mount at `/` would briefly flash MainComponent.
  // auth-error / no-access still take over so sign-in failures surface.
  readonly displaySplashScreen = computed(() => {
    const base = this.splashScreen();
    return base === 'spinner' && this.bootsToStandaloneRoute ? 'standalone-loading' : base;
  });

  authErrorTitle = AUTH_ERROR_TITLE;
  authErrorHint = AUTH_ERROR_HINT;
  authErrorRetry = AUTH_ERROR_RETRY;

  constructor() {
    // Initialize the app
    effect(() => {
      const ergsClaims = this.authService.ergsClaims();
      const logInProgress = this.authService.loginProgress();

      if (ergsClaims && !logInProgress) {
        // Handle stored redirect (navigation concern — e.g. deep link before login)
        let domain: string | null = null;
        let imp: string | null = null;
        let target: UrlTree | null = null;
        const redirect = window.localStorage.getItem('redirectAfterLogin');
        if (redirect) {
          window.localStorage.removeItem('redirectAfterLogin');
          const tree = this.router.parseUrl(redirect);
          const params = tree.queryParamMap;
          // Impersonation is URL-driven but the params are stripped from the
          // address bar below, and a refresh reloads with a clean URL. So: URL
          // wins (a fresh impersonation, or a deep link like the replay tab),
          // and when absent we restore the still-valid persisted one so the
          // session survives F5. See impersonation.ts.
          const urlImp = params.get('imp');
          const urlDomain = params.get('domain');
          if (urlImp || urlDomain) {
            imp = urlImp;
            domain = urlDomain;
            persistImpersonation({ imp, domain });
          } else {
            const restored = restoreImpersonation();
            imp = restored.imp;
            domain = restored.domain;
          }
          const ps: Record<string, unknown> = {};
          Object.keys(tree.queryParams).forEach((key) => {
            if (key !== 'imp' && key !== 'domain') {
              ps[key] = tree.queryParams[key];
            }
          });
          target = new UrlTree(tree.root, ps, tree.fragment);
        }

        // Router has `withDisabledInitialNavigation()` set in app.config —
        // the first call here is the initial navigation. Without that, the
        // router would resolve `<origin>?code=...` to MainComponent at
        // `path: ''`, briefly open its `<ion-modal class="no-access-modal">`
        // (because `!bootCompleted` while the boot chain is in flight) and
        // then unmount when we navigateByUrl below — leaving an orphan
        // overlay in <body>. Triggering navigation here, AFTER reading the
        // deep-link target, skips the transient MainComponent mount.
        if (!this._routerInitialized) {
          this._routerInitialized = true;
          void this.router.navigateByUrl(target ?? this.router.parseUrl('/'));
        } else if (target) {
          // Subsequent claims refresh that ALSO carries a redirect — only
          // happens on a full re-login round-trip (idle / token expiry),
          // since the localStorage entry is consumed on first read.
          void this.router.navigateByUrl(target);
        }

        // Always initialize app data — not gated by redirect.
        // This ensures re-login after idle (or any auth recovery) works.
        this.ds.setupAccessData(ergsClaims, this.authService.accessToken, domain, imp);
      }
    });

    // User access status
    effect(() => {
      const expired = this.authService.expiredToken();
      this.ds.setTokenExpiredStatus(expired);
    });

    effect(() => {
      const idle = this.authService.idleUser();
      this.ds.setUserIdleStatus(idle);
    });

    // Load flights once SignalR setup has resolved.
    // __connectSignalR patches signalRConnectionData.loaded = true after calling
    // signalR.connect(), so this fires once regardless of whether the hub connects.
    effect(() => {
      const signalRData = this.ds.signalRConnectionData();
      if (signalRData.loaded) {
        this.ds.loadFlights();
      }
    });

    // SignalR hint: a flight was updated → re-fetch flight list
    effect(() => {
      const movementLogId = this.signalR.flightUpdated();
      if (movementLogId) {
        this.ds.loadFlights();
        // If the updated flight is currently selected, refresh its details
        // (comments + events) live instead of asking the user to reopen it.
        if (this.fs.selectedFlightMlId() === movementLogId) {
          this.fs.refreshSelectedFlightDetails();
        }
      }
    });

    // SignalR hint: a comment was added → refresh chat if viewing that flight
    effect(() => {
      const event = this.signalR.commentAdded();
      if (event && this.fs.selectedFlightMlId() === event.movementLogId) {
        this.fs.loadFlightDetails();
      }
    });
  }

  // Retry button on the sign-in error screen: re-runs the OAuth authorize flow.
  retryLogin() {
    this.authService.retryLogin();
  }

  logout() {
    this.authService.logout();
  }

  ngOnInit() {
    void this.platform.ready().then(() => {
      // this.platform.isPortrait() is based on the screen width and height
      // screen.orientation.type is based on the actual screen orientation, use this
      this.rs.setDeviceInfo(this.platform.platforms(), screen.orientation.type);
    });
    this.watchScreenOrientationChange();
  }

  watchScreenOrientationChange() {
    if ('orientation' in window.screen) {
      window.screen.orientation.onchange = () => {
        this.rs.setScreenOrientation(screen.orientation.type);
      };
    }
  }
}
